// The prototype's built-in course (bhop_prototype_v1), in Source coordinates (X north, Y west, Z up).
// The prototype built it in three.js coordinates; mapping is Source (X, Y, Z) = three (-z, -x, y),
// and every value here is the exact image of the prototype's value.

import { BhopBlock } from '../../sim/bhopBlocks';
import { BoxWorld, type Box } from '../../sim/collision/boxWorld';
import { CONTENTS_SOLID, type SolidEntity } from '../../sim/collision/types';
import { DIST_EPSILON } from '../../sim/constants';
import type { SpawnPoint } from '../../sim/player';
import type { Level } from '../../sim/simulation';
import type { Zone } from '../../sim/zones';

export type SolidType = 'start' | 'block' | 'checkpoint' | 'end';

export interface CourseSolid {
  box: Box;
  type: SolidType;
  block?: BhopBlock;
}

export interface Course extends Level {
  name: string;
  solids: CourseSolid[];
  startZone: Zone;
  endZone: Zone;
}

type Dir = readonly [number, number];
const N: Dir = [1, 0];
const E: Dir = [0, -1];
const yawOf = (d: Dir) => Math.atan2(d[1], d[0]);

export function buildCourse(): Course {
  const world = new BoxWorld();
  const solids: CourseSolid[] = [];
  const blocks: BhopBlock[] = [];

  /** Box centred on (cx, cy) horizontally, `top` = upper face height. */
  function makeBox(
    cx: number,
    cy: number,
    top: number,
    sx: number,
    sy: number,
    thick: number,
    type: SolidType,
  ) {
    const entity: SolidEntity = { contents: CONTENTS_SOLID };
    const box = world.add([cx - sx / 2, cy - sy / 2, top - thick], [cx + sx / 2, cy + sy / 2, top], entity);
    const solid: CourseSolid = { box, type };
    solids.push(solid);
    return solid;
  }

  let bx = 0;
  let by = 0;
  let prevHalf = 192;
  makeBox(0, 0, 0, 384, 384, 32, 'start');
  const startZone: Zone = { min: [-192, -192, -1], max: [192, 192, 128] };

  function seg(
    dir: Dir,
    n: number,
    size: number,
    g0: number,
    g1: number,
    wob: (i: number) => number,
    heights?: number[],
  ) {
    // Right-hand perpendicular of dir.
    const perp = [dir[1], -dir[0]];
    for (let i = 0; i < n; i++) {
      const gap = g0 + (g1 - g0) * (n > 1 ? i / (n - 1) : 0);
      const dist = prevHalf + gap + size / 2;
      bx += dir[0] * dist;
      by += dir[1] * dist;
      const off = wob(i);
      const top = heights ? heights[i % heights.length] : 0;
      const s = makeBox(bx + perp[0] * off, by + perp[1] * off, top, size, size, 16, 'block');
      const blk = new BhopBlock(s.box);
      s.block = blk;
      s.box.entity.block = blk;
      blocks.push(blk);
      prevHalf = size / 2;
    }
  }

  function plat(dir: Dir, gap: number, size: number, type: SolidType) {
    const dist = prevHalf + gap + size / 2;
    bx += dir[0] * dist;
    by += dir[1] * dist;
    prevHalf = size / 2;
    return makeBox(bx, by, 0, size, size, 32, type);
  }

  seg(N, 10, 112, 96, 150, (i) => Math.sin(i * 1.1) * 56);
  const c1 = plat(N, 150, 256, 'checkpoint');
  c1.box.entity.checkpoint = { pos: [bx, by, DIST_EPSILON], yaw: yawOf(E), stage: 2 };
  seg(E, 12, 104, 130, 196, (i) => (i % 2 ? 1 : -1) * 64, [0, 12, 0, -12]);
  const c2 = plat(E, 190, 256, 'checkpoint');
  c2.box.entity.checkpoint = { pos: [bx, by, DIST_EPSILON], yaw: yawOf(N), stage: 3 };
  seg(N, 12, 96, 160, 226, (i) => Math.sin(i * 0.8) * 80, [0, 0, 16, 16, 0, 0, -16, -16]);
  const end = plat(N, 200, 384, 'end');
  const endZone: Zone = {
    min: [end.box.min[0], end.box.min[1], -1],
    max: [end.box.max[0], end.box.max[1], 128],
  };

  const spawn: SpawnPoint = { pos: [-130, 0, DIST_EPSILON], yaw: 0, stage: 1 };
  return { name: 'bhop_prototype_v1', world, solids, blocks, spawn, startZone, endZone, stageCount: 3 };
}
